import React, { useState, useEffect, useRef } from 'react';
import { Box, Text, Progress, HStack } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const expandVariants = {
  collapsed: { height: 0, opacity: 0 },
  expanded: { height: 'auto', opacity: 1 },
};

const RefreshProgressCard = ({ progress, onComplete }) => {
  const [expanded, setExpanded] = useState(false);
  const completedRef = useRef(false);
  const hideTimerRef = useRef(null);
  const [visible, setVisible] = useState(true);

  const { aggregated, configs = [] } = progress || {};
  const percentage = aggregated?.percentage ?? 0;
  const isComplete = percentage >= 100;
  const isMulti = configs.length > 1;

  useEffect(() => {
    if (isComplete && !completedRef.current) {
      completedRef.current = true;
      hideTimerRef.current = setTimeout(() => {
        setVisible(false);
        onComplete?.();
      }, 2000);
    }
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [isComplete, onComplete]);

  useEffect(() => {
    completedRef.current = false;
    setVisible(true);
  }, [progress?.aggregated?.total]);

  if (!progress?.isRefreshing || !visible) return null;

  const totalFields =
    configs.reduce((sum, c) => sum + (c.fieldCount || 0), 0) ||
    aggregated?.total ||
    0;
  const totalFilings = aggregated?.total || 0;

  const summaryText = isComplete
    ? 'Refresh complete'
    : `Refreshing${totalFields ? ` ${totalFields} field${totalFields !== 1 ? 's' : ''}` : ''} across ${totalFilings} filing${totalFilings !== 1 ? 's' : ''}`;

  return (
    <Box
      bg="#111111"
      border="1px solid rgba(255,255,255,0.10)"
      boxShadow="0 8px 32px rgba(0,0,0,0.4)"
      borderRadius="10px"
      p={3}
      mx={3}
      mb={2}
    >
      {/* Aggregated bar */}
      <HStack justify="space-between" mb={1.5}>
        <Text fontSize="xs" color="#a3a3a3" fontWeight="500">
          {summaryText}
        </Text>
        <HStack spacing={1}>
          <Text
            fontSize="xs"
            color={isComplete ? 'rgba(74,222,128,0.85)' : 'rgba(255,255,255,0.6)'}
            fontWeight="600"
          >
            {Math.round(percentage)}%
          </Text>
          {isMulti && (
            <Box
              as="button"
              onClick={() => setExpanded((v) => !v)}
              display="flex"
              alignItems="center"
              p={0.5}
              borderRadius="4px"
              _hover={{ bg: 'rgba(255,255,255,0.06)' }}
              transition="transform 200ms ease"
              transform={expanded ? 'rotate(180deg)' : 'rotate(0deg)'}
            >
              <ChevronDown size={14} color="rgba(255,255,255,0.35)" />
            </Box>
          )}
        </HStack>
      </HStack>

      <Progress
        value={percentage}
        size="xs"
        borderRadius="full"
        bg="rgba(255,255,255,0.08)"
        transition="all 300ms ease"
        sx={{
          '& > div': {
            bg: isComplete ? 'rgba(74,222,128,0.8)' : 'rgba(255,255,255,0.65)',
            transition: 'all 300ms ease',
          },
        }}
      />

      {/* Per-config breakdown */}
      <AnimatePresence initial={false}>
        {expanded && isMulti && (
          <motion.div
            key="config-detail"
            initial="collapsed"
            animate="expanded"
            exit="collapsed"
            variants={expandVariants}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <Box mt={3} pl={1}>
              {configs.map((c) => {
                const pct = c.percentage ?? 0;
                return (
                  <Box key={c.configId} mb={2}>
                    <HStack justify="space-between" mb={0.5}>
                      <Text
                        fontSize="xs"
                        color="rgba(255,255,255,0.5)"
                        noOfLines={1}
                        maxW="70%"
                      >
                        {c.label || c.configId.slice(0, 8)}
                      </Text>
                      <Text fontSize="xs" color="rgba(255,255,255,0.35)">
                        {Math.round(pct)}%
                      </Text>
                    </HStack>
                    <Progress
                      value={pct}
                      size="xs"
                      borderRadius="full"
                      bg="rgba(255,255,255,0.06)"
                      h="3px"
                      sx={{
                        '& > div': {
                          bg: pct >= 100 ? 'rgba(74,222,128,0.8)' : 'rgba(255,255,255,0.55)',
                        },
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default RefreshProgressCard;
